import type { ReactElement } from 'react'
import { cloneElement } from 'react'

interface AffiliateTrackableProps {
  seller: string
  sellerType: string
  release: string
  position: string
  cta: string
  text: string
  testName: string
  url: string
  impression?: boolean
  segment?: number
  /** The destination came from an author's `affiliate_link_override`, so `url`
   *  points somewhere the baked offer doesn't — a retailer guide linking to
   *  Amazon while `seller` still names the brand's own store. `seller` can't be
   *  derived from the override without inventing attribution, so flag the click
   *  instead and let analytics segment it. The old app marked these too
   *  (`data-event-cta: 'override'` in button-helpers.ts); we use a dedicated
   *  attribute so `cta` keeps carrying the button label. */
  override?: boolean
  children: ReactElement<Record<string, unknown>>
}

export const AffiliateTrackable = ({
  seller,
  sellerType,
  release,
  position,
  cta,
  text,
  testName,
  url,
  impression,
  segment,
  override,
  children,
}: AffiliateTrackableProps) => {
  const attrs: Record<string, string | boolean> = {
    'data-track': true,
    'data-event-name': 'AffiliateAction',
    'data-event-seller': seller,
    'data-affiliate-seller-type': sellerType,
    'data-event-product': release,
    'data-event-position': position,
    'data-affiliate-cta': cta,
    'data-affiliate-text': text,
    'data-affiliate-test-name': testName,
    'data-event-url': url,
  }

  if (impression) {
    attrs['data-track-impression'] = true
  }
  if (segment !== undefined) {
    attrs['data-affiliate-segment'] = String(segment)
  }
  if (override) {
    attrs['data-affiliate-override'] = true
  }

  return cloneElement(children, attrs)
}
